import { useEffect } from "react";
import { isAfter, parseISO, subDays, subMinutes } from "date-fns";
import { useEventCallback } from "usehooks-ts";

import { useEventTracker } from "./useEventTracker";

const KEY_PREFIX = "treatment";

/**
 * Google Analytics sessions are 30 minutes long, which means that we
 * need to ensure that treatment events are dispatched with enough
 * regularity to reasonably be assured that a treatment event is
 * attached to each session.
 */
export const EVENT_DEBOUNCE_DURATION = 30; // minutes

export const useSplitGoogleEventPublisher = () => {
  const { publishEvent } = useEventTracker();

  const publishTreatmentEvent = useEventCallback(
    (name: string, treatment: string) =>
      publishEvent({
        action: "impression",
        category: `split test ${name}`,
        label: `group ${treatment}`,
        nonInteractionEvent: true,
      }),
  );

  /**
   * When the window regains focus, we need to iterate through any previously
   * recorded events in order to re-emit them. Google Analytics potentially
   * treats a browser window that has been open but idle for longer than 30
   * minutes to be a new session, and we want to ensure those new sessions also
   * have a record of any treatments.
   *
   * In order to limit how many events are recorded, we'll have a lower cutoff
   * of one day. This should provide enough of a view into which tests/flags a
   * session has been exposed to without re-emitting every event ever seen.
   */
  const onWindowFocus = useEventCallback(() => {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(KEY_PREFIX)) {
        continue;
      }

      const entry = window.localStorage.getItem(key);
      if (!entry) {
        continue;
      }

      const entryDate = parseISO(entry);

      // Check if the date is older than EVENT_DEBOUNCE_DURATION but younger than one day
      if (
        isAfter(entryDate, subDays(new Date(), 1)) &&
        isAfter(subMinutes(new Date(), EVENT_DEBOUNCE_DURATION), entryDate)
      ) {
        // Extract name and treatment from the key (format: "treatment:name:treatment")
        const parts = key.split(":");
        if (parts.length === 3) {
          const [, name, treatment] = parts;

          publishTreatmentEvent(name, treatment);
        }
      }
    }
  });

  useEffect(() => {
    window.addEventListener("focus", onWindowFocus);

    return () => {
      window.removeEventListener("focus", onWindowFocus);
    };
  }, []);

  return (name: string, treatment: string) => {
    /**
     * Control treatments likely only indicate that the test has yet
     * to start, which isn't interesting information to record in Google
     * Analytics. To reduce our overall event count, we'll skip these events.
     */
    if (treatment === "control") {
      return;
    }

    const seenKey = [KEY_PREFIX, name, treatment].join(":");
    const entry = window.localStorage.getItem(seenKey);

    if (
      !entry ||
      isAfter(subMinutes(new Date(), EVENT_DEBOUNCE_DURATION), parseISO(entry))
    ) {
      window.localStorage.setItem(seenKey, new Date().toISOString());

      publishTreatmentEvent(name, treatment);
    }
  };
};
